// 2026-10-23 addition (clinician invite-only build, Piece 4) — the
// mandatory, non-skippable gate an invited clinician completes on first
// login: name, practice, NPI, phone, signature + consent. Reuses
// Register.js's own clinician-field markup/validation conventions and
// SignaturePad/useNpiConfirm hooks rather than rebuilding them, since the
// fields are identical — only the submission target and post-login
// context differ (this page is authenticated; Register.js's clinician
// branch is now unreachable, per Piece 1).
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Checkbox, FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import UserContext from "./UserContext";
import { useNpiConfirm } from "./addressShared";
import SignaturePad from "./SignaturePad";
import { extractErrorDetail, isValidNpi } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };

function Field({ label, required, hint, isDark, children }) {
  const sub = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.06em", color: sub, textTransform: "uppercase" }}>
          {label}
          {required && <Box component="span" sx={{ color: BRAND.gold, ml: 0.4 }}>*</Box>}
        </Typography>
        {hint && <Typography sx={{ fontSize: 14, color: sub }}>{hint}</Typography>}
      </Box>
      {children}
    </Box>
  );
}

export default function CompleteProfile() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);

  // 2026-10-26 fix (confirmed real regression, user-reported — "why does
  // clinician signup still ask for first name and last name") — the
  // backend side of this fix (ClinicianCompleteProfileRequest/
  // _clinician_identity_key, see their own 2026-10-26 comments) was
  // changed to a single `name` field weeks... same day, but this form —
  // the actual thing a clinician fills out — was never updated to
  // match, so every real submission was sending first_name/last_name to
  // an endpoint that no longer accepts them. Single field now, matching
  // the backend exactly.
  const [form, setForm] = useState({ name: "", practice_name: "", npi: "", phone: "" });
  const [signatureConsent, setSignatureConsent] = useState(false);
  const [consentText, setConsentText] = useState("");
  const [message, setMessage] = useState(null);
  const [isConflict, setIsConflict] = useState(false);
  const [loading, setLoading] = useState(false);
  const signatureRef = useRef(null);
  const { confirmNpi, dialog: npiConfirmDialog } = useNpiConfirm(API_BASE);

  useEffect(() => {
    if (consentText) return;
    axios.get(`${API_BASE}/api/clinician-signature-consent-text`)
      .then((res) => setConsentText(res.data.consent_text || ""))
      .catch(() => setConsentText(""));
  }, [consentText]);

  const f = (key) => ({ value: form[key], onChange: (e) => setForm((p) => ({ ...p, [key]: e.target.value })) });

  // 2026-10-26 addition — NPPES (the government provider registry) is
  // the one place that still genuinely needs a first/last split (its
  // own schema splits a provider's name) — best-effort only, mirrors
  // the backend's own _split_name_for_npi_check exactly (first token /
  // last token, middle tokens dropped), never used for the real
  // identity match itself, which now always uses the single name
  // directly.
  const splitNameForNpiCheck = (name) => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length < 2) return ["", parts[0] || ""];
    return [parts[0], parts[parts.length - 1]];
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,35,96,0.018)",
      fontSize: 16,
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,53,122,0.3)" },
      "&.Mui-focused fieldset": { borderColor: BRAND.navy, borderWidth: "2px" },
      "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,53,122,0.08)" },
    },
    "& .MuiOutlinedInput-input": { py: "13px", px: "14px" },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setIsConflict(false);

    if (!form.name.trim())
      return setMessage({ severity: "error", text: "Please enter your name." });
    if (!form.practice_name.trim())
      return setMessage({ severity: "error", text: "Practice name is required." });
    // 2026-10-23 UI-review fix — was a bare 10-digit regex, which let a
    // 10-digit phone number slip through as a "valid-looking" NPI,
    // triggering the NPPES confirm-dialog round trip only to fail with a
    // 422 afterward. Real check-digit validation now fails fast, before
    // that dialog ever opens.
    if (!isValidNpi(form.npi))
      return setMessage({ severity: "error", text: "Please enter a valid 10-digit NPI." });
    if (!form.phone.trim())
      return setMessage({ severity: "error", text: "Phone number is required." });

    const signaturePngBase64 = signatureRef.current?.getPngBase64();
    if (!signaturePngBase64)
      return setMessage({ severity: "error", text: "Please draw your signature." });
    if (!signatureConsent)
      return setMessage({ severity: "error", text: "Please consent to using your signature on test order requisitions." });

    const [_npiFirst, _npiLast] = splitNameForNpiCheck(form.name);
    const npiRegistryConfirmed = await confirmNpi(form.npi.trim(), _npiFirst, _npiLast);

    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/clinician/complete-profile`,
        {
          name: form.name.trim(),
          practice_name: form.practice_name.trim(),
          npi: form.npi.trim(),
          phone: form.phone.trim(),
          signature_png_base64: signaturePngBase64,
          signature_consent: signatureConsent,
          npi_registry_confirmed: npiRegistryConfirmed,
        },
        { headers: { Authorization: `Bearer ${user?.token}` } },
      );
      // Clears identity_profile_incomplete and surfaces the now-known
      // practice_name/npi, matching what email_login already returns for
      // a completed clinician — so the rest of the portal's clinician UI
      // works immediately, no forced re-login needed.
      setUser({
        ...user,
        identity_profile_incomplete: false,
        practice_name: res.data?.practice_name || form.practice_name.trim(),
        npi: res.data?.npi || form.npi.trim(),
      });
      navigate("/home", { replace: true });
    } catch (err) {
      setLoading(false);
      // 2026-10-23 UI-review fix — _require_pending_clinician 404s when
      // this account has already completed its profile (e.g. a
      // double-submit, or finishing in a second tab while this one's
      // cached state went stale). That's not an error from the
      // clinician's perspective — they're already done — so redirect
      // straight to /home instead of showing a raw "Not found." message
      // that would look like a broken page.
      if (err.response?.status === 404) {
        setUser({ ...user, identity_profile_incomplete: false });
        navigate("/home", { replace: true });
        return;
      }
      setIsConflict(err.response?.status === 409);
      setMessage({ severity: "error", text: extractErrorDetail(err, "Could not complete your profile.") });
    }
  };

  return (
    <Box sx={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      bgcolor: isDark ? "#0b1220" : "#f4f6fb", px: 2, py: 6,
    }}>
      {npiConfirmDialog}
      <Box sx={{
        width: { xs: "100%", sm: 520 }, maxWidth: 520, borderRadius: 3, overflow: "hidden",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
        boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5)" : "0 24px 80px rgba(0,53,122,0.12)",
        bgcolor: isDark ? "#111827" : "#ffffff",
      }}>
        <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})` }} />
        <Box sx={{ px: 4, pt: 4, pb: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 0.75 }}>
            Complete Your Profile
          </Typography>
          <Typography sx={{ fontSize: 14.5, color: isDark ? "#94a3b8" : "#64748b", lineHeight: 1.6, mb: 3 }}>
            Before you continue, we need a few details to set up your clinician account.
          </Typography>

          {message && (
            <Alert severity={message.severity} sx={{ mb: 2.5 }}>
              {message.text}
              {isConflict && (
                <>
                  {" "}Contact us at{" "}
                  <Box component="a" href="mailto:info@labarium.com" sx={{ color: "inherit", fontWeight: 600 }}>
                    info@labarium.com
                  </Box>.
                </>
              )}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <Field label="Full name" required isDark={isDark}>
              <TextField hiddenLabel fullWidth required placeholder="Jane Roe" sx={inputSx} {...f("name")} />
            </Field>

            <Field label="Practice / Organization" required isDark={isDark}>
              <TextField hiddenLabel fullWidth required placeholder="Labarium Health Systems"
                sx={inputSx} {...f("practice_name")} />
            </Field>

            <Field label="NPI number" required isDark={isDark} hint="10 digits">
              <TextField hiddenLabel fullWidth required placeholder="1234567890"
                slotProps={{ htmlInput: { maxLength: 10 } }} sx={inputSx} {...f("npi")} />
            </Field>

            <Field label="Phone number" required isDark={isDark} hint="For urgent result callbacks">
              <TextField hiddenLabel type="tel" fullWidth required placeholder="+1 555 123 4567"
                sx={inputSx} {...f("phone")} />
            </Field>

            <Field label="Your Signature" required isDark={isDark}
              hint="Used to sign test order requisitions you submit">
              <SignaturePad ref={signatureRef} isDark={isDark} />
            </Field>
            <FormControlLabel
              sx={{ alignItems: "flex-start", mt: 0.5, mb: 3, ml: 0 }}
              control={
                <Checkbox
                  checked={signatureConsent}
                  onChange={(e) => setSignatureConsent(e.target.checked)}
                  sx={{ color: BRAND.navy, mt: -0.5, "&.Mui-checked": { color: BRAND.navy } }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", lineHeight: 1.6 }}>
                  {consentText || "By drawing and submitting your signature, you consent to Labarium using it to sign test order requisitions you submit through your account."}
                </Typography>
              }
            />

            <Button type="submit" fullWidth variant="contained" disabled={loading}
              sx={{ bgcolor: BRAND.navy, py: 1.5, fontWeight: 700, textTransform: "none", fontSize: 15.5 }}>
              {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Complete Profile"}
            </Button>
          </form>
        </Box>
      </Box>
    </Box>
  );
}
